import { useState, useEffect } from 'react';
import { useRoleCheck } from '@/hooks/use-role-check';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, X, Info } from 'lucide-react';

const MFA_PROMPT_DISMISSED_KEY = 'mfa_prompt_dismissed';
const MFA_PROMPT_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * MFA Recommendation Prompt
 * Encourages administrators to enable Multi-Factor Authentication
 * Tracks MFA adoption and provides guidance
 */
export function MFAPrompt() {
  const { data: roleData } = useRoleCheck();
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasMFA, setHasMFA] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Only show to super_admin and admin users
  const shouldShow = roleData?.isSuperAdmin || roleData?.isAdmin;

  useEffect(() => {
    if (!shouldShow) {
      setIsLoading(false);
      return;
    }

    const checkMFAStatus = async () => {
      try {
        // Check if user has MFA enabled
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }

        // Check if user has enrolled MFA factors
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const mfaEnabled = factors?.totp && factors.totp.length > 0;
        
        setHasMFA(mfaEnabled);

        // Check if prompt was dismissed recently
        const dismissed = localStorage.getItem(MFA_PROMPT_DISMISSED_KEY);
        if (dismissed) {
          const dismissedTime = parseInt(dismissed, 10);
          const timeSinceDismissal = Date.now() - dismissedTime;
          
          if (timeSinceDismissal < MFA_PROMPT_DISMISS_DURATION) {
            setIsLoading(false);
            return;
          }
        }

        // Show prompt if MFA is not enabled and not recently dismissed
        setShowPrompt(!mfaEnabled);
      } catch (error) {
        console.error('Failed to check MFA status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkMFAStatus();
  }, [shouldShow]);

  const handleDismiss = () => {
    localStorage.setItem(MFA_PROMPT_DISMISSED_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  const handleEnableMFA = () => {
    // Navigate to account settings (you may need to adjust this URL)
    window.open('https://supabase.com/dashboard/project/_/settings/auth', '_blank');
    handleDismiss();
  };

  if (isLoading || !shouldShow || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 max-w-md z-50">
      <Card className="border-2 border-primary/20 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Enable Multi-Factor Authentication</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-1"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            Recommended for administrator accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Multi-factor authentication (MFA) adds an extra layer of security to your
              account by requiring a second verification step when signing in.
            </AlertDescription>
          </Alert>
          
          <div className="text-sm space-y-2">
            <p className="font-medium">Benefits of MFA:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Protects against password theft</li>
              <li>Reduces risk of unauthorized access</li>
              <li>Required for sensitive operations</li>
              <li>Industry best practice</li>
            </ul>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button onClick={handleEnableMFA} className="flex-1">
            <Shield className="h-4 w-4 mr-2" />
            Enable MFA
          </Button>
          <Button variant="outline" onClick={handleDismiss}>
            Remind Later
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
